/* eslint-disable no-nested-ternary */
import dayjs from 'dayjs';
import { InboundProcess } from '@features/supplyChain/types';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { PalletCalculator } from '@components/PalletCalculator';
import { useAuth } from '@context';
import { UserRoleService } from '@services';
import { SkuInfoLink } from '@tailwindComponents/SkuInfoModal';
import { Button } from '../../../tailwindComponents/Button/Button';

interface ShipmentNoteDetailsProps {
  selectedInboundProcess: InboundProcess;
  onClose: () => void;
  primaryAction?: () => void;
  embedded?: boolean;
  isDesktop: boolean;
  isTablet: boolean;
}

function CopyIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <rect width='14' height='14' x='8' y='8' rx='2' ry='2' />
      <path d='M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2' />
    </svg>
  );
}

function ShipperIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2' />
      <circle cx='9' cy='7' r='4' />
      <path d='M22 21v-2a4 4 0 0 0-3-3.87' />
      <path d='M16 3.13a4 4 0 0 1 0 7.75' />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <rect width='18' height='18' x='3' y='4' rx='2' ry='2' />
      <line x1='16' x2='16' y1='2' y2='6' />
      <line x1='8' x2='8' y1='2' y2='6' />
      <line x1='3' x2='21' y1='10' y2='10' />
    </svg>
  );
}

function WarehouseIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M22 8.35V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8.35A2 2 0 0 1 3.26 6.5l8-3.2a2 2 0 0 1 1.48 0l8 3.2A2 2 0 0 1 22 8.35Z' />
      <path d='M6 18h12' />
      <path d='M6 14h12' />
      <rect width='12' height='12' x='6' y='10' />
    </svg>
  );
}

function NoteAddIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='18'
      height='18'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z' />
      <polyline points='14 2 14 8 20 8' />
      <line x1='12' x2='12' y1='18' y2='12' />
      <line x1='9' x2='15' y1='15' y2='15' />
    </svg>
  );
}

export function ShipmentNoteDetails({
  selectedInboundProcess,
  onClose,
  primaryAction,
  embedded,
  isDesktop,
  isTablet,
}: ShipmentNoteDetailsProps) {
  const { shipmentNote, goodsReceivedNote, notices } = selectedInboundProcess;
  const { user } = useAuth();
  const { isWarehouseManager } = UserRoleService();

  const hasIssues = (notices?.length ?? 0) > 0;
  const isProcessComplete = !!goodsReceivedNote?.finalisedAt;
  const hasIssuesBeforeProcessIsComplete = hasIssues && !isProcessComplete;

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(shipmentNote.shipmentNoteNumber);
  };

  return (
    <div className='flex flex-col'>
      {/* Document Reference Badge */}
      <div className='px-6 pt-2 pb-4'>
        <div className='inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-md border border-gray-200'>
          <span className='text-sm font-medium text-gray-700'>
            Shipment note #{shipmentNote.shipmentNoteNumber}
          </span>
          <button
            type='button'
            onClick={handleCopyToClipboard}
            className='text-gray-500 hover:text-gray-700 transition-colors'
            aria-label='Copy shipment note number'
          >
            <CopyIcon />
          </button>
        </div>
      </div>

      {/* Metadata Fields */}
      <div
        className={`grid gap-4 px-6 py-4 border-b border-gray-200 ${
          isDesktop ? 'grid-cols-4' : isTablet ? 'grid-cols-2' : 'grid-cols-1'
        }`}
      >
        {/* Created by */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500'>
            <ShipperIcon />
            <span className='text-xs font-medium'>Created by</span>
          </div>
          <span className='text-sm font-medium text-gray-900'>
            {shipmentNote.createdByName}
          </span>
        </div>

        {/* ETA */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500'>
            <CalendarIcon />
            <span className='text-xs font-medium'>ETA</span>
          </div>
          <span className='text-sm font-medium text-gray-900'>
            {dayjs
              .utc(shipmentNote.estimatedTimeOfArrival.dateTime)
              .tz(shipmentNote.estimatedTimeOfArrival.timeZone)
              .format('D MMM YYYY')}
          </span>
        </div>

        {/* Warehouse Name */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500'>
            <WarehouseIcon />
            <span className='text-xs font-medium'>Warehouse name</span>
          </div>
          <span className='text-sm font-medium text-gray-900'>
            {shipmentNote.warehouse?.name}
          </span>
        </div>

        {/* Creation date */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500'>
            <CalendarIcon />
            <span className='text-xs font-medium'>Creation date</span>
          </div>
          <span className='text-sm font-medium text-gray-900'>
            {dayjs(shipmentNote.createdAt).format('D MMM YYYY')}
          </span>
        </div>
      </div>

      {/* Additional Info Row */}
      <div
        className={`grid gap-4 px-6 py-4 border-b border-gray-200 ${
          isDesktop ? 'grid-cols-4' : isTablet ? 'grid-cols-2' : 'grid-cols-1'
        }`}
      >
        {/* Client name (for warehouse managers) */}
        {isWarehouseManager(user) && (
          <div className='flex flex-col gap-1'>
            <div className='flex items-center gap-1.5 text-gray-500'>
              <ShipperIcon />
              <span className='text-xs font-medium'>Client name</span>
            </div>
            <span className='text-sm font-medium text-gray-900'>
              {selectedInboundProcess.userBusinessName}
            </span>
          </div>
        )}

        {/* Warehouse Address */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500'>
            <WarehouseIcon />
            <span className='text-xs font-medium'>Warehouse address</span>
          </div>
          <span className='text-sm font-medium text-gray-900'>
            {shipmentNote.warehouse?.address}
          </span>
        </div>

        {/* Date of arrival (if goods received) */}
        {goodsReceivedNote?.finalisedAt && (
          <div className='flex flex-col gap-1'>
            <div className='flex items-center gap-1.5 text-gray-500'>
              <CalendarIcon />
              <span className='text-xs font-medium'>Date of arrival</span>
            </div>
            <span className='text-sm font-medium text-gray-900'>
              {dayjs(goodsReceivedNote.finalisedAt).format('D MMM YYYY')}
            </span>
          </div>
        )}
      </div>

      {/* Issues */}
      {hasIssues && (
        <div className='px-6 py-4 border-b border-gray-200'>
          <p className='text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-2'>
            Issues
          </p>
          <div className='space-y-2'>
            {notices?.map((notice) => (
              <div
                key={notice}
                className={`flex items-center gap-2 p-2 rounded ${
                  hasIssuesBeforeProcessIsComplete ? 'bg-amber-50' : 'bg-red-50'
                }`}
              >
                <ExclamationTriangleIcon
                  className={`w-4 h-4 flex-shrink-0 ${
                    hasIssuesBeforeProcessIsComplete
                      ? 'text-amber-600'
                      : 'text-red-600'
                  }`}
                />
                <span
                  className={`text-sm ${
                    hasIssuesBeforeProcessIsComplete
                      ? 'text-amber-800'
                      : 'text-red-800'
                  }`}
                >
                  {notice}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SKU Table */}
      <div className='px-6 py-4'>
        {/* Table Header - Desktop only */}
        {isDesktop && (
          <div className='grid grid-cols-[2.5rem_1fr_1fr_1fr_1fr] gap-4 pb-3 border-b border-gray-200 mb-4'>
            <span className='text-xs font-semibold text-gray-500 text-center'>
              SKU #
            </span>
            <span className='text-xs font-semibold text-gray-500'>SKU ID</span>
            <span className='text-xs font-semibold text-gray-500'>
              Product name
            </span>
            <span className='text-xs font-semibold text-gray-500'>Pallets</span>
            <span className='text-xs font-semibold text-gray-500'>
              SKU Q-ty
            </span>
          </div>
        )}

        {/* SKU Rows */}
        <div className='flex flex-col gap-4'>
          {shipmentNote.skus.map((sku, index) => (
            <div
              key={sku.skuId}
              className={`${
                index !== shipmentNote.skus.length - 1
                  ? 'pb-4 border-b border-gray-100'
                  : ''
              }`}
            >
              {isDesktop ? (
                /* Desktop Layout */
                <div className='grid grid-cols-[2.5rem_1fr_1fr_1fr_1fr] gap-4 items-center'>
                  {/* Index */}
                  <div className='flex justify-center'>
                    <span className='inline-flex items-center justify-center w-7 h-7 text-xs font-medium text-gray-700 bg-gray-100 rounded-full'>
                      {index + 1}
                    </span>
                  </div>
                  {/* SKU ID */}
                  <span className='text-sm text-gray-900'>
                    <SkuInfoLink inventorySkuId={sku.skuId}>
                      {sku.skuIdentifier}
                    </SkuInfoLink>
                  </span>
                  {/* Product name */}
                  <div className='flex flex-col'>
                    <span className='text-sm text-gray-900'>{sku.name}</span>
                  </div>
                  {/* Pallets */}
                  <div className='text-sm text-gray-900'>
                    <PalletCalculator
                      palletName={sku.packagingDetails.packagingInfo.type}
                      skuQuantity={sku.skuQuantity}
                      amountOfSkusInLayer={
                        sku.packagingDetails.packagingInfo.amountOfSkusInLayer
                      }
                      amountOfLayersInPallet={
                        sku.packagingDetails.packagingInfo
                          .amountOfLayersInPallet
                      }
                    />
                  </div>
                  {/* SKU Quantity */}
                  <span className='text-sm text-gray-900'>
                    {sku.skuQuantity}
                  </span>
                </div>
              ) : (
                /* Mobile/Tablet Layout */
                <div className='flex gap-3'>
                  {/* Index */}
                  <div className='flex-shrink-0'>
                    <span className='inline-flex items-center justify-center w-7 h-7 text-xs font-medium text-gray-700 bg-gray-100 rounded-full'>
                      {index + 1}
                    </span>
                  </div>
                  {/* Details */}
                  <div
                    className={`flex-1 grid gap-3 ${
                      isTablet ? 'grid-cols-2' : 'grid-cols-1'
                    }`}
                  >
                    <div className='flex flex-col gap-0.5'>
                      <span className='text-xs font-medium text-gray-500'>
                        SKU ID
                      </span>
                      <span className='text-sm text-gray-900'>
                        <SkuInfoLink inventorySkuId={sku.skuId}>
                          {sku.skuIdentifier}
                        </SkuInfoLink>
                      </span>
                    </div>
                    <div className='flex flex-col gap-0.5'>
                      <span className='text-xs font-medium text-gray-500'>
                        Product name
                      </span>
                      <span className='text-sm text-gray-900'>{sku.name}</span>
                    </div>
                    <div className='flex flex-col gap-0.5'>
                      <span className='text-xs font-medium text-gray-500'>
                        Pallets
                      </span>
                      <div className='text-sm text-gray-900'>
                        <PalletCalculator
                          palletName={sku.packagingDetails.packagingInfo.type}
                          skuQuantity={sku.skuQuantity}
                          amountOfSkusInLayer={
                            sku.packagingDetails.packagingInfo
                              .amountOfSkusInLayer
                          }
                          amountOfLayersInPallet={
                            sku.packagingDetails.packagingInfo
                              .amountOfLayersInPallet
                          }
                        />
                      </div>
                    </div>
                    <div className='flex flex-col gap-0.5'>
                      <span className='text-xs font-medium text-gray-500'>
                        SKU Q-ty
                      </span>
                      <span className='text-sm text-gray-900'>
                        {sku.skuQuantity}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Comments Section */}
      {shipmentNote.comment && (
        <div className='px-6 py-4 border-t border-gray-200'>
          <h3 className='text-sm font-semibold text-gray-900 mb-2'>Comment</h3>
          <p className='text-sm text-gray-600'>{shipmentNote.comment}</p>
        </div>
      )}

      {!embedded && (
        <div
          className={`flex gap-3 px-6 py-4 mt-4 border-t border-gray-200 ${
            isDesktop ? 'flex-row justify-end' : 'flex-col-reverse'
          }`}
        >
          <Button
            label='Close'
            intent='secondary'
            size='sm'
            onClick={onClose}
            className={!isDesktop ? 'w-full' : ''}
          />
          {!selectedInboundProcess?.goodsReceivedNote && primaryAction && (
            <Button
              label='Create draft goods received note'
              size='sm'
              startIcon={<NoteAddIcon />}
              onClick={primaryAction}
              className={!isDesktop ? 'w-full' : ''}
            />
          )}
        </div>
      )}
    </div>
  );
}
